import { defineStore } from 'pinia';
import type { RouteLocationNormalized } from 'vue-router';
import { useConnectionStore } from '@/stores/connection';
import { API } from '@vp/common';

export interface RootState {
  route?: RouteLocationNormalized;
  currentAccountId: number;
  _apiBase?: string;
  _apiGraphqlEndpoint?: string;
  defaultApiBase?: string;
  config?: API.VPConfig.VPConfig;
}

interface VPAppConfig {
  apiBase: string;
  apiGraphqlEndpoint?: string;
  currentAccountId: number;
}

declare global {
  interface Window {
    vpAppConfig?: VPAppConfig;
  }
}

const getApiBase = (): string | undefined => {
  const appConfig = !import.meta.env.SSR ? window.vpAppConfig : undefined;

  if (appConfig?.apiBase) {
    return appConfig.apiBase;
  } else {
    console.warn('missing window.vpAppConfig.apiBase ');
  }

  return import.meta.env.VITE_API_BASE;
};

const API_BASE_KEY = 'vp-checkout-api-base';

const getStoredApiBase = (): string | undefined => {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem(API_BASE_KEY) || undefined;
  }
};

const storeApiBase = (address?: string) => {
  if (typeof window !== 'undefined') {
    if (address) {
      sessionStorage.setItem(API_BASE_KEY, address);
    } else {
      sessionStorage.removeItem(API_BASE_KEY);
    }
  }
};

const getGqlApiEndpoint = (): string | undefined => {
  const appConfig = !import.meta.env.SSR ? window.vpAppConfig : undefined;

  if (appConfig?.apiGraphqlEndpoint) {
    return appConfig.apiGraphqlEndpoint;
  } else {
    console.warn('missing window.vpAppConfig.gqlApiEndpoint');
  }

  return import.meta.env.VITE_GQL_API_ENDPOINT;
};

export const useRootStore = defineStore('root', {
  state: (): RootState => ({
    route: undefined,
    _apiBase: getStoredApiBase(),
    defaultApiBase: getApiBase(),
    _apiGraphqlEndpoint: getGqlApiEndpoint(),
    currentAccountId: (typeof window !== 'undefined' ? window.vpAppConfig?.currentAccountId : null) || 0,
  }),
  getters: {
    apiBase(state: RootState): string {
      const base = state._apiBase || state.defaultApiBase || '';
      return base;
    },

    apiGraphqlEndpoint(state: RootState): string | undefined {
      return state._apiGraphqlEndpoint;
    },

    isDefaultApiBase(state: RootState): boolean {
      return this.apiBase == state.defaultApiBase || !state.defaultApiBase;
    },
  },
  actions: {
    SET_CURRENT_ROUTE(route: RouteLocationNormalized) {
      this.route = route;
    },

    setApiBase(address: string | undefined) {
      // console.debug('setting server address to:', address)
      this._apiBase = address;
      storeApiBase(address);
    },

    async loadConfig() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient;

      if (!apiClient) {
        throw new Error('apiClient not initialized');
      }
      this.config = await API.getConfig(apiClient);
    },
  },
});
